import { useCallback, useEffect, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { TripsClient } from "@/features/trips/TripsClient";
import { useTranslation } from "react-i18next";
import { createTrip, deleteTrip, listTrips, sendToScoretrace, tripsKeys, updateTrip, type TripListItem, type TripStatus, type TripsSearch } from "@/features/trips/trips.api";
import { todayIso } from "@/features/trips/tripLabels";
import { usePlanningErrorText } from "@/features/planning/LegMenu";
import { useSendToBoard } from "@/features/trips/sendToBoard";
import { applyParamUpdates } from "@/lib/searchParams";
import { filterSearch } from "@/lib/columnFilters";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { recallPlanDay, rememberListSearch } from "@/lib/listState";

export const Route = createFileRoute("/_shell/trips/")({
  // Reading is tms_planning; making trips is tms_planning_write.
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_planning") && !hasModule(context.session, "tms_planning_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): TripsSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      date: str(search.date),
      status: str(search.status),
      q: str(search.q),
      sort: str(search.sort),
      order: str(search.order),
      cursor: str(search.cursor),
    };
  },
  component: TripsScreen,
});

function TripsScreen() {
  const planningErrorText = usePlanningErrorText();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { session } = Route.useRouteContext();
  const canPlan = session.roles.includes("admin") || hasModule(session, "tms_planning_write");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const { t } = useTranslation();

  // A trip list is a day; without one in the URL the plan day of the Planbord and Ritten (lib/listState), else today.
  const date = search.date ?? recallPlanDay() ?? todayIso();

  useEffect(() => {
    // The day holds until midnight, as on the board (lib/listState); a day chosen here is the plan day the board opens on.
    rememberListSearch("/trips", search, { days: ["date"] });
  }, [search]);

  const query = useQuery({
    queryKey: tripsKeys.list({ ...search, date }),
    queryFn: () => listTrips({ ...search, date }),
    placeholderData: keepPreviousData,
    // Planning is shared work: what a colleague puts on a lorry shows up here.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  async function newTrip() {
    setError(null);
    try {
      const created = await createTrip({ planDate: date });
      await queryClient.invalidateQueries({ queryKey: tripsKeys.all });
      navigate({ to: "/trips/$id", params: { id: created.id } });
    } catch (err) {
      setError(planningErrorText(err));
    }
  }

  // What the right mouse button on a row does (TripRowMenu.tsx). Both read the list again,
  // so the row shows what happened even when the trip is not open.
  async function setStatus(id: string, status: TripStatus) {
    setError(null);
    try {
      await updateTrip(id, { status });
      await queryClient.invalidateQueries({ queryKey: tripsKeys.all });
    } catch (err) {
      setError(planningErrorText(err));
    }
  }

  // The loading of the picked trips to Scoretrace (docs/tms-crossdock.md CD13): what they load in Rijkevoort.
  async function toScoretrace(trips: TripListItem[]) {
    setError(null);
    setNotice(null);
    try {
      const result = await sendToScoretrace(trips.map((trip) => trip.id));
      const skipped = result.skipped.map((one) => one.tripNumber ?? "").filter(Boolean).join(", ");
      setNotice(
        [t("trips.scoretraceSent", { count: result.sent.length }), skipped ? t("trips.scoretraceSkipped", { trips: skipped }) : ""]
          .filter(Boolean)
          .join(" "),
      );
      await queryClient.invalidateQueries({ queryKey: tripsKeys.all });
    } catch (err) {
      setError(planningErrorText(err));
    }
  }

  // Now to the on-board computer (P85): the trip's log says it once it went.
  const sendToBoardNow = useSendToBoard();
  async function sendToBoard(trip: { id: string; tripNumber: string }) {
    setError(null);
    setNotice(null);
    const said = await sendToBoardNow(trip);
    if (said.error) setError(said.error);
    else setNotice(said.notice ?? null);
    await queryClient.invalidateQueries({ queryKey: tripsKeys.all });
  }

  async function throwAway(id: string) {
    setError(null);
    try {
      await deleteTrip(id);
      await queryClient.invalidateQueries({ queryKey: tripsKeys.all });
    } catch (err) {
      setError(planningErrorText(err));
    }
  }

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Trips">
      {error && (
        <div className="wl-alert wl-alert--danger" style={{ marginBottom: "var(--space-3)" }} role="alert">
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="wl-alert wl-alert--info" style={{ marginBottom: "var(--space-3)" }} role="status">
          <span>{notice}</span>
        </div>
      )}
      <TripsClient
        items={query.data?.data ?? []}
        pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
        search={search}
        date={date}
        updateParams={updateParams}
        onOpenTrip={(id) => navigate({ to: "/trips/$id", params: { id } })}
        onNewTrip={() => void newTrip()}
        onSetStatus={(id, status) => void setStatus(id, status)}
        onDeleteTrip={(id) => void throwAway(id)}
        onSendToScoretrace={(trips) => void toScoretrace(trips)}
        onSendToBoard={(trip) => void sendToBoard(trip)}
        onShowOnBoard={(trip: TripListItem) => navigate({ to: "/planning", search: { date: trip.planDate } })}
        isFetching={query.isFetching}
        isLoading={query.isLoading}
        canPlan={canPlan}
        userId={session.userId}
      />
    </div>
  );
}
